import { useImperativeHandle, type Ref } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { RawHtmlNode } from "./RawHtmlNode";
import { VariableNode, type EditorVariable } from "./VariableNode";
import VariablePlugin, { INSERT_VARIABLE_COMMAND } from "./VariablePlugin";
import HtmlPlugin, { RESET_HTML_COMMAND } from "./HtmlPlugin";
import Toolbar from "./Toolbar";
import VariablePicker from "./VariablePicker";
import styles from "./Editor.module.css";
import Text from "../UI/Text.tsx";

const NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  CodeNode,
  LinkNode,
  RawHtmlNode,
  VariableNode,
];

const THEME = {
  text: {
    bold: "editor-bold",
    italic: "editor-italic",
    underline: "editor-underline",
    strikethrough: "editor-strikethrough",
  },
  quote: "editor-blockquote",
  code: "editor-code",
  link: "editor-link",
};

export interface EditorHandle {
  insertVariable: (key: string) => void;
  isFocused: () => boolean;
  reset: (html?: string | null) => void;
}

interface Props {
  defaultValue?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  availableVariables?: ReadonlyArray<EditorVariable>;
  showVariablePicker?: boolean;
  ref?: Ref<EditorHandle>;
}

function EditorVariablePicker({
  available,
}: {
  available?: ReadonlyArray<EditorVariable>;
}) {
  const [editor] = useLexicalComposerContext();
  return (
    <VariablePicker
      available={available}
      label="Variables disponibles dans le corps"
      onInsert={(key) => editor.dispatchCommand(INSERT_VARIABLE_COMMAND, key)}
    />
  );
}

function ImperativeHandlePlugin({ ref }: { ref?: Ref<EditorHandle> }) {
  const [editor] = useLexicalComposerContext();
  useImperativeHandle(
    ref,
    () => ({
      insertVariable: (key: string) =>
        editor.dispatchCommand(INSERT_VARIABLE_COMMAND, key),
      isFocused: () =>
        editor.getRootElement()?.contains(document.activeElement) ?? false,
      reset: (html?: string | null) =>
        editor.dispatchCommand(RESET_HTML_COMMAND, html),
    }),
    [editor],
  );
  return null;
}

function Editor({
  defaultValue,
  onChange,
  placeholder = "Saisissez votre texte…",
  availableVariables,
  showVariablePicker = true,
  ref,
}: Props) {
  return (
    <LexicalComposer
      initialConfig={{
        namespace: "editor",
        nodes: NODES,
        theme: THEME,
        onError: (error: Error) => {
          console.error("Lexical editor error", {}, error);
        },
      }}
    >
      <VariablePlugin available={availableVariables ?? []}>
        <ImperativeHandlePlugin ref={ref} />
        {showVariablePicker && (
          <EditorVariablePicker available={availableVariables} />
        )}
        <div className={styles.editor}>
          <Toolbar />
          <div className={styles.editorContainer}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={styles.editorInput}
                  aria-label="Éditeur de texte enrichi"
                />
              }
              placeholder={
                <Text as="div" color="muted" className={styles.placeholder}>
                  {placeholder}
                </Text>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <HtmlPlugin
            defaultValue={defaultValue}
            onChange={onChange}
            availableVariables={availableVariables}
          />
        </div>
      </VariablePlugin>
    </LexicalComposer>
  );
}

export default Editor;
